import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  ViewEncapsulation
} from '@angular/core';
import {Observation, Project, Publication} from 'asax-common';
import {Subscription, switchMap, timer} from 'rxjs';
import {CellSimilarityService} from '../cell-similarity.service';
import {map, tap} from 'rxjs/operators';

const delay = 300;

@Component({
  selector: 'asax-result-table-cell-similarity-container',
  templateUrl: './cell-similarity-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .similarity-table.ngx-datatable.material .datatable-body .datatable-body-row .datatable-body-cell {
      font-size: 12px !important;
      padding: 0.4rem 0.8rem !important;
    }

    .similarity-table.ngx-datatable.material .datatable-body .datatable-body-row .datatable-body-group-cell {
      font-size: 12px !important;
      padding: 0.4rem 0.8rem !important;
    }

    .similarity-table.ngx-datatable.material .datatable-header .datatable-header-cell {
      font-size: 10px !important;
      padding: 0.4rem 0.8rem !important;
    }
  `
  ],
  encapsulation: ViewEncapsulation.None
})
export class CellSimilarityContainerComponent {
  @Input() row: Observation | Project | Publication;

  similarProjects: Project[];
  similarPublications: Publication[];
  tabId: string = 'projects';

  isLoading: boolean;

  private timerSubscription: Subscription;

  constructor(private readonly ref: ChangeDetectorRef,
              private readonly cellSimilarityService: CellSimilarityService) {
  }

  fetchSimilarDocuments(): void {
    if ('code' in this.row) {
      this.fetchSimilarProjectsForProject();
    } else if ('bibCode' in this.row) {
      this.fetchSimilarPublicationsForPublication();
    } else if ('datasetId' in this.row) {
      this.fetchSimilarProjectsAndPublicationsForObservation();
    }
  }

  private fetchSimilarProjectsAndPublicationsForObservation(): void {
    this.timerSubscription = timer(delay).pipe(
      tap(() => this.isLoading = true),
      tap(() => this.ref.markForCheck()),
      switchMap(() => this.cellSimilarityService.fetchSimilarProjectsForObservation((this.row as Observation)?.datasetId)),
      map((similarProjects) => similarProjects.filter(project => project.code !== (this.row as Observation)?.project?.code)),
      map((similarProjects) => similarProjects.slice(0, 20)), //Make sure that only first 20 similar projects are dispalyed
      tap((similarProjects) => this.similarProjects = similarProjects),
      switchMap(() => this.cellSimilarityService.fetchSimilarPublicationsForObservation((this.row as Observation)?.datasetId)),
      tap((similarPublications) => this.similarPublications = similarPublications),
      tap(() => this.isLoading = false),
      tap(() => this.ref.markForCheck())
    ).subscribe();
  }

  private fetchSimilarPublicationsForPublication(): void {
    this.timerSubscription = timer(delay).pipe(
      tap(() => this.isLoading = true),
      tap(() => this.ref.markForCheck()),
      switchMap(() => this.cellSimilarityService.fetchSimilarPublicationsForPublication((this.row as Publication)?.bibCode)),
      map((similarPublications) => similarPublications.filter(publication => publication.bibCode !== (this.row as Publication)?.bibCode)),
      map((similarPublications) => similarPublications.slice(0, 20)), //Make sure that only first 20 similar Publications are dispalyed
      tap((similarPublications) => this.similarPublications = similarPublications),
      tap(() => this.isLoading = false),
      tap(() => this.ref.markForCheck())
    ).subscribe();
  }

  private fetchSimilarProjectsForProject(): void {
    this.timerSubscription = timer(delay).pipe(
      tap(() => this.isLoading = true),
      tap(() => this.ref.markForCheck()),
      switchMap(() => this.cellSimilarityService.fetchSimilarProjectsForProject((this.row as Project)?.code)),
      map((similarProjects) => similarProjects.filter(project => project.code !== (this.row as Project)?.code)),
      map((similarProjects) => similarProjects.slice(0, 20)), //Make sure that only first 20 similar projects are dispalyed
      tap((similarProjects) => this.similarProjects = similarProjects),
      tap(() => this.isLoading = false),
      tap(() => this.ref.markForCheck())
    ).subscribe();
  }

  onTabChanged(tabId: string) {
    this.tabId = tabId;
  }

  @HostListener('mouseenter')
  private onMouseEnter(): void {
    this.fetchSimilarDocuments();
  }

  @HostListener('mouseleave')
  private onMouseLeave(): void {
    this.timerSubscription?.unsubscribe();
  }
}
